import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DatumObracunaPdv, IRacunDto, TipRacuna, VrstaRacuna } from '@kodit/core/data-api';
import { RacunService } from '@kodit/core/services';
import { FormGroupTypeSafe } from 'angular-typesafe-reactive-forms-helper';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kodit-osnovne-informacije-racuna',
  templateUrl: './osnovne-informacije-racuna.component.html',
  styleUrls: ['./osnovne-informacije-racuna.component.scss'],
})
export class OsnovneInformacijeRacunaComponent implements OnInit, OnDestroy {
  /** Subs */
  private _datumPrometaChangeSub: Subscription = new Subscription();
  private _datumValuteChangeSub: Subscription = new Subscription();
  private _rokPlacanjaChangeSub: Subscription = new Subscription();
  private _rokVazenjaChangeSub: Subscription = new Subscription();
  private _subs: Subscription = new Subscription();

  /** Sprečava kružno ažuriranje datumPrometa/rokPlacanja/datumValute polja */
  private _isSyncingDatumValute = false;

  /** Props */

  /** Flags */
  displayDatumObracunaPdv: boolean;
  jeDokumentOPovecanju: boolean;
  jeAvansaFaktura: boolean;
  jeFaktura: boolean;
  datumObracunaDanPrometa: DatumObracunaPdv;
  jeDokumentOSmanjenju: boolean;
  jeIzlazna: boolean;
  jeUlazna: boolean;
  jePredracun: boolean;
  jeOtpremnica: boolean;

  /** I/O */
  @Input() racunFormGroup: FormGroupTypeSafe<IRacunDto>;
  @Input() rokVazenjaCtrl: AbstractControl;
  @Input() uSistemuPDV!: boolean;

  constructor(private _route: ActivatedRoute) { }

  ngOnInit(): void {
    this._setFlags();
    this._setSubs();
    if (this.racunFormGroup.value.tip == TipRacuna.PREDRACUN
      || this.racunFormGroup.value.tip == TipRacuna.PONUDA) {
      this.rokPlacanjaChanged(this.rokVazenjaCtrl.value);
    }

    if (this.racunFormGroup.controls.datumObracunaPdv.value == DatumObracunaPdv.EMPTY && this.racunFormGroup.controls.tip.value == TipRacuna.AVANSNI_RACUN) {
      this.racunFormGroup.controls.datumObracunaPdv.patchValue(DatumObracunaPdv.NA_DAN_PLACANJA);
    }
    if (this.racunFormGroup.controls.rokPlacanja.value < 0) {
      this.racunFormGroup.controls.rokPlacanja.setValue(0);
    }

    if (this.displayDatumObracunaPdv && !this.jeEditMode) {
      if (this.uSistemuPDV) {
        this.racunFormGroup.controls.pdvObavezaZaIzdavaoca.patchValue(true);
      } else {
        this.racunFormGroup.controls.pdvObavezaZaIzdavaoca.patchValue(false);
      }
    }

    if (this.jeDokumentOPovecanju && this.racunFormGroup.controls.datumObracunaPdv.value === DatumObracunaPdv.NA_DAN_IZDAVANJA) {
      this.racunFormGroup.controls.datumUgovora.reset();
    }

    this._subs.add(
      this.racunFormGroup.controls.pdvObavezaZaIzdavaoca?.valueChanges.subscribe((res) => {
        if (!res && !this.jeDokumentOPovecanju) {
          this.racunFormGroup.controls.datumObracunaPdv.patchValue(DatumObracunaPdv.BEZ_OBAVEZE);
        } else if (res && this.jeAvansaFaktura) {
          this.racunFormGroup.controls.datumObracunaPdv.patchValue(DatumObracunaPdv.NA_DAN_PLACANJA);
        } else if (res && this.racunFormGroup.value.tip === TipRacuna.IZLAZNA_FAKTURA) {
          this.racunFormGroup.controls.datumObracunaPdv.patchValue(DatumObracunaPdv.NA_DAN_PROMETA);
        }
      })
    );

    this._subs.add(
      this.racunFormGroup.controls.datumObracunaPdv?.valueChanges.subscribe((res) => {
        if (this.jeDokumentOPovecanju && res == DatumObracunaPdv.NA_DAN_IZDAVANJA) {
          this.racunFormGroup.controls.datumUgovora.reset();
        } else if (this.jeDokumentOPovecanju && res == DatumObracunaPdv.NA_DAN_PROMETA) {
          this.racunFormGroup.controls.datumUgovora.patchValue(new Date());
        }
      })
    );
  }

  rokPlacanjaChanged(rokPlacanja: number) {
    if (!this.racunFormGroup.value.datumPrometa) {
      return;
    }
    this._calculateDatumValute(
      this.racunFormGroup.value.datumPrometa,
      rokPlacanja
    );
  }

  datumPrometaChanged(datumIzdavanja: Date) {
    if (!datumIzdavanja) {
      return;
    }
    this._calculateDatumValute(
      datumIzdavanja,
      this.racunFormGroup.value.rokPlacanja
    );
  }

  private _calculateDatumValute(date: Date, days: number) {
    const newValue = new Date(date);
    newValue.setDate(date.getDate() + days);
    this._isSyncingDatumValute = true;
    this.racunFormGroup.controls.datumValute.patchValue(newValue);
    this.racunFormGroup.controls.datumValute.updateValueAndValidity();
    this._isSyncingDatumValute = false;
  }

  private _datumValuteChanged(datumValute: Date) {
    if (
      this._isSyncingDatumValute ||
      !datumValute ||
      !this.racunFormGroup.value.datumPrometa ||
      this.jeAvansaFaktura ||
      this.jeDokumentOPovecanju ||
      this.jeDokumentOSmanjenju
    ) {
      return;
    }
    const rokPlacanja = this._daysBetween(this.racunFormGroup.value.datumPrometa, datumValute);
    this._isSyncingDatumValute = true;
    this.racunFormGroup.controls.rokPlacanja.patchValue(rokPlacanja);
    this._isSyncingDatumValute = false;
  }

  private _daysBetween(from: Date, to: Date): number {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);
    return Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
  }

  get jeEditMode(): boolean {
    return this._route.snapshot.data.racun?.racunDto?.id;
  }

  private _setFlags() {
    this.datumObracunaDanPrometa = DatumObracunaPdv.NA_DAN_PROMETA;

    this.jeIzlazna = this.racunFormGroup.value.vrsta === VrstaRacuna.IZLAZNI;
    this.jeUlazna = this.racunFormGroup.value.vrsta === VrstaRacuna.ULAZNI;

    this.jeFaktura = this.racunFormGroup.value.tip === TipRacuna.IZLAZNA_FAKTURA
      || this.racunFormGroup.value.tip === TipRacuna.ULAZNA_FAKTURA;
    this.jeAvansaFaktura = this.racunFormGroup.value.tip === TipRacuna.AVANSNI_RACUN;
    this.jeDokumentOSmanjenju = this.racunFormGroup.value.tip === TipRacuna.DOKUMENT_O_SMANJENJU;
    this.jeDokumentOPovecanju = this.racunFormGroup.value.tip === TipRacuna.DOKUMENT_O_POVECANJU;
    this.jePredracun = this.racunFormGroup.value.tip === TipRacuna.PREDRACUN;
    this.jeOtpremnica = this.racunFormGroup.value.tip === TipRacuna.OTPREMNICA;

    this.displayDatumObracunaPdv =
      this.racunFormGroup.value.tip === TipRacuna.IZLAZNA_FAKTURA || this.jeAvansaFaktura || this.jeDokumentOPovecanju;
  }

  private _setSubs() {
    this._rokPlacanjaChangeSub = this.racunFormGroup.controls.rokPlacanja.valueChanges.subscribe(
      (rokPlacanja) => {
        if (this.racunFormGroup.value.tip !== TipRacuna.PONUDA && this.racunFormGroup.value.tip !== TipRacuna.PREDRACUN) {
          if (this.jeEditMode && this.jeDokumentOPovecanju) {
            return;
          }
          this.rokPlacanjaChanged(rokPlacanja);
        }
      }
    );

    this._rokVazenjaChangeSub = this.rokVazenjaCtrl?.valueChanges.subscribe(
      (rokVazenja) => {
        this.rokPlacanjaChanged(rokVazenja);
      }
    );

    this._datumPrometaChangeSub = this.racunFormGroup.controls.datumPrometa.valueChanges.subscribe(
      (datumIzdavanja) => {
        this.datumPrometaChanged(datumIzdavanja);
      }
    );

    this._datumValuteChangeSub = this.racunFormGroup.controls.datumValute.valueChanges.subscribe(
      (datumValute) => {
        this._datumValuteChanged(datumValute);
      }
    );
  }

  setPdvObaveza(value: boolean): void {
    if (!this.uSistemuPDV && value) { 
      return;
    }
    
    this.racunFormGroup.controls.pdvObavezaZaIzdavaoca.patchValue(value);
  }

  ngOnDestroy(): void {
    this._datumPrometaChangeSub.unsubscribe();
    this._datumValuteChangeSub.unsubscribe();
    this._rokPlacanjaChangeSub.unsubscribe();
    this._rokVazenjaChangeSub?.unsubscribe();
    this._subs?.unsubscribe();
  }
}
